import { Injectable } from '@angular/core';
import { LLCTransaction } from '@models/llc-mortgages';
import {
	BusinessMatterClientWithAssociations,
	Matter,
	MatterAdjustment,
	MatterTitle,
	PersonMatterClientWithAssociations,
} from '@models/matter';
import { StepMilestone } from 'src/app/shared/atomic/molecules/step-progress-nav/step-progress-nav.component';
import { SyncRequirementsService } from './builders/sync-requirements.service';
import { StepCalculationsBuilder } from './builders/step-calculations.service';
import {
	getLLCLenderType,
	LLCLenderType,
} from 'src/app/shared/services/mortgages/llc.service';
import { MatterMortgage } from '@models/mortgage';
import { NameFormattingSettings } from '@models/businessSettings/settings';
import { Jurisdiction } from '@models/jurisdictions';
export interface StepperItems {
	step0ItemSections: StepMilestone[];
	step1ItemSections: StepMilestone[];
	step2ItemSections: StepMilestone[];
}

const DefaultStepperItems = (): StepperItems => ({
	step0ItemSections: [],
	step1ItemSections: [],
	step2ItemSections: [],
});

@Injectable({
	providedIn: 'root',
})
export class StepperSectionsBuilderService {
	constructor(
		private syncReqService: SyncRequirementsService,
		private stepperBuilder: StepCalculationsBuilder,
	) {}

	getTDSections(
		mortgage: LLCTransaction,
		matter: Matter,
		adjustments: MatterAdjustment[],
		matterMortgage: MatterMortgage,
		clientAssociations: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		guarantors: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		matterTitles: MatterTitle[],
		nameFormattingSettings: NameFormattingSettings,
		titleInsuranceEffectiveDate: string,
		jurisdiction: Jurisdiction,
	): StepperItems {
		if (!(matter?.id && mortgage)) {
			return DefaultStepperItems();
		}

		return {
			step0ItemSections: this.syncReqService.getSyncRequirements(
				mortgage,
				matter,
				matterMortgage?.id,
				clientAssociations,
				guarantors,
			),
			step1ItemSections: this.getTDRequestForFundsRequirements(
				matter,
				getLLCLenderType(mortgage.lender),
				clientAssociations,
				mortgage,
				matterMortgage,
				matterTitles,
				nameFormattingSettings,
			),
			step2ItemSections: this.getTDFinalReportRequirements(
				matter,
				adjustments,
				matterMortgage,
				matterTitles,
				titleInsuranceEffectiveDate,
				jurisdiction,
			),
		};
	}

	getBNSSections(
		mortgage: LLCTransaction,
		matter: Matter,
		matterMortgage: MatterMortgage,
		clientAssociations: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		guarantors: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		matterTitles: MatterTitle[],
		nameFormattingSettings: NameFormattingSettings,
		titleInsuranceEffectiveDate: string,
		jurisdiction: Jurisdiction,
	): StepperItems {
		if (!(matter?.id && mortgage)) {
			return DefaultStepperItems();
		}

		return {
			step0ItemSections: this.syncReqService.getSyncRequirements(
				mortgage,
				matter,
				matterMortgage?.id,
				clientAssociations,
				guarantors,
			),
			step1ItemSections: this.getBSNRequestForFundsRequirements(
				matter,
				getLLCLenderType(mortgage.lender),
				clientAssociations,
				mortgage,
				matterMortgage,
				nameFormattingSettings,
			),
			step2ItemSections: this.getBSNFinalReportRequirements(
				matter,
				matterMortgage,
				matterTitles,
				titleInsuranceEffectiveDate,
				jurisdiction,
			),
		};
	}

	getMMSSections(
		mortgage: LLCTransaction,
		matter: Matter,
		adjustments: MatterAdjustment[],
		matterMortgage: MatterMortgage,
		clientAssociations: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		guarantors: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		matterTitles: MatterTitle[],
		nameFormattingSettings: NameFormattingSettings,
		jurisdiction: Jurisdiction,
	): StepperItems {
		if (!(matter?.id && mortgage)) {
			return DefaultStepperItems();
		}

		return {
			step0ItemSections: this.syncReqService.getSyncRequirements(
				mortgage,
				matter,
				matterMortgage?.id,
				clientAssociations,
				guarantors,
			),
			step1ItemSections: this.getMMSRequestForFundsRequirements(
				matter,
				adjustments,
				clientAssociations,
				matterTitles,
				nameFormattingSettings,
				jurisdiction,
			),
			step2ItemSections: this.geMMSFinalReportRequirements(
				matter,
				matterMortgage,
			),
		};
	}

	getTDRequestForFundsRequirements(
		matter: Matter,
		lenderType: LLCLenderType,
		clientAssociations: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		mortgage: LLCTransaction,
		matterMortgage: MatterMortgage,
		matterTitles: MatterTitle[],
		nameFormattingSettings: NameFormattingSettings,
	): StepMilestone[] {
		const result: StepMilestone[] = [];
		result.push(this.stepperBuilder.getMortgageInformation(matter));
		result.push(
			this.stepperBuilder.getTDRequestForFundsRegistrationDetails(
				matter,
				matterTitles,
			),
		);
		result.push(
			...this.stepperBuilder.getBorrowersDetailsComplete(
				matter.id,
				clientAssociations,
				nameFormattingSettings,
			),
		);
		result.push(
			...this.stepperBuilder.getVerifiedClientIds(
				matter?.id,
				clientAssociations,
				lenderType,
				nameFormattingSettings,
			),
		);

		this.guarantorTasks(
			matter,
			lenderType,
			clientAssociations,
			mortgage,
			matterMortgage,
			result,
			nameFormattingSettings,
		);
		this.signingOfficerTasks(
			matter,
			lenderType,
			clientAssociations,
			result,
			nameFormattingSettings,
		);

		return result;
	}

	getTDFinalReportRequirements(
		matter: Matter,
		adjustments: MatterAdjustment[],
		matterMortgage: MatterMortgage,
		matterTitles: MatterTitle[],
		titleInsuranceEffectiveDate: string,
		jurisdiction: Jurisdiction,
	) {
		const result: StepMilestone[] = [];
		result.push(
			this.stepperBuilder.getTDPropertyDetails(
				matter,
				adjustments,
				matterTitles,
				jurisdiction,
			),
		);
		result.push(
			this.stepperBuilder.getFinalReportRegistrationDetails(
				matter,
				matterMortgage,
			),
		);
		result.push(this.stepperBuilder.getFireInsuranceDetails(matter));
		result.push(
			this.stepperBuilder.getTitleInsuranceDetails(
				matter,
				titleInsuranceEffectiveDate,
			),
		);
		result.push(this.stepperBuilder.getPropertyTaxDetails(matter));

		return result;
	}

	getBSNRequestForFundsRequirements(
		matter: Matter,
		lenderType: LLCLenderType,
		clientAssociations: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		mortgage: LLCTransaction,
		matterMortgage: MatterMortgage,
		nameFormattingSettings: NameFormattingSettings,
	): StepMilestone[] {
		const result: StepMilestone[] = [];

		result.push(this.stepperBuilder.getMortgageInformation(matter));
		result.push(
			...this.stepperBuilder.getBorrowersDetailsComplete(
				matter.id,
				clientAssociations,
				nameFormattingSettings,
			),
		);
		result.push(
			this.stepperBuilder.getBNSRequestForFundsRegistrationDetails(matter),
		);
		result.push(
			...this.stepperBuilder.getVerifiedClientIds(
				matter?.id,
				clientAssociations,
				lenderType,
				nameFormattingSettings,
			),
		);

		this.guarantorTasks(
			matter,
			lenderType,
			clientAssociations,
			mortgage,
			matterMortgage,
			result,
			nameFormattingSettings,
		);

		this.signingOfficerTasks(
			matter,
			lenderType,
			clientAssociations,
			result,
			nameFormattingSettings,
		);

		return result;
	}

	getBSNFinalReportRequirements(
		matter: Matter,
		matterMortgage: MatterMortgage,
		matterTitles: MatterTitle[],
		titleInsuranceEffectiveDate,
		jurisdiction: Jurisdiction,
	) {
		const result: StepMilestone[] = [];
		result.push(
			this.stepperBuilder.getBNSPropertyDetails(
				matter,
				matterTitles,
				jurisdiction,
			),
		);
		result.push(
			this.stepperBuilder.getFinalReportRegistrationDetails(
				matter,
				matterMortgage,
			),
		);
		result.push(this.stepperBuilder.getFireInsuranceDetails(matter));
		result.push(
			this.stepperBuilder.getTitleInsuranceDetails(
				matter,
				titleInsuranceEffectiveDate,
			),
		);

		return result;
	}

	getMMSRequestForFundsRequirements(
		matter: Matter,
		adjustments: MatterAdjustment[],
		clientAssociations: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		matterTitles: MatterTitle[],
		nameFormattingSettings: NameFormattingSettings,
		jurisdiction: Jurisdiction,
	): StepMilestone[] {
		const result: StepMilestone[] = [];
		result.push(this.stepperBuilder.getMortgageInformation(matter));
		result.push(
			this.stepperBuilder.getTDPropertyDetails(
				matter,
				adjustments,
				matterTitles,
				jurisdiction,
			),
		);
		result.push(
			...this.stepperBuilder.getBorrowersDetailsComplete(
				matter.id,
				clientAssociations,
				nameFormattingSettings,
			),
		);
		// TODO: Add solicitor details here
		result.push(this.stepperBuilder.getPropertyTaxDetails(matter));

		return result;
	}

	geMMSFinalReportRequirements(matter: Matter, matterMortgage: MatterMortgage) {
		const result: StepMilestone[] = [];
		result.push(this.stepperBuilder.getMMsFinalReportPropertyDetails(matter));
		result.push(
			this.stepperBuilder.getFinalReportRegistrationDetails(
				matter,
				matterMortgage,
			),
		);

		return result;
	}

	guarantorTasks = (
		matter: Matter,
		lenderType: LLCLenderType,
		clientAssociations: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		mortgage: LLCTransaction,
		matterMortgage: MatterMortgage,
		tasks: StepMilestone[],
		nameFormattingSettings: NameFormattingSettings,
	): void => {
		const guarantorTasks = this.stepperBuilder.getGuarantorsDetailsComplete(
			matter.id,
			clientAssociations,
			nameFormattingSettings,
		);
		if (guarantorTasks && guarantorTasks?.length > 0) {
			tasks.push(...guarantorTasks);
			const guarantorsIdTasks = this.stepperBuilder.getVerifiedGuarantorIds(
				matter?.id,
				matterMortgage?.id,
				clientAssociations,
				lenderType,
				nameFormattingSettings,
				mortgage?.data?.guarantors?.length,
			);
			tasks.push(...guarantorsIdTasks);
		}
	};

	signingOfficerTasks = (
		matter: Matter,
		lenderType: LLCLenderType,
		clientAssociations: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		tasks: StepMilestone[],
		nameFormattingSettings: NameFormattingSettings,
	): void => {
		const signingOfficerTasks =
			this.stepperBuilder.getSigningOfficersDetailsCompleted(
				matter.id,
				clientAssociations,
			);
		if (signingOfficerTasks.tasks.length > 0) {
			tasks.push(signingOfficerTasks);
			const signingOfficerIdTasks =
				this.stepperBuilder.getVerifiedSigningOfficerIds(
					matter.id,
					clientAssociations,
					lenderType,
					nameFormattingSettings,
				);
			tasks.push(...signingOfficerIdTasks);
		}
	};
}
